<script setup lang="ts">
import {computed} from "vue";
import EpisodRef from "../Models/EpisodRef";
import Session from "../Models/Session";
import EpisodRefs from "../Models/EpisodRefs";
import SanctuarySession from "../Models/SanctuarySession";
import SanctuaryEstablishment from "../Models/SanctuaryEstablishment";
import Week from "../../Models/Week";
import SanctuaryCompany from "../Models/SanctuaryCompany";
import type { SportSession } from "@/composables/useSportSessions";
import ParticipantBadges from "../../ParticipantBadges.vue";

interface Props {
    date: string,
    tabId: string,
    active?: boolean,
    planning: {[key: number]: SanctuarySession[]},
    establishments: SanctuaryEstablishment[],
    refs: { weeks: Week[], companies: SanctuaryCompany[]},
    mySessions?: SportSession[]
}

const props = withDefaults(defineProps<Props>(), {active: false});

const hourRange = Array.from({ length: 22 - 6 + 1 }, (_, i) => i + 6)

const sessionsByEstablishmentAndTime = computed<{[key: string]: SanctuarySession[]}>(() => {
    let detailledPlanning: {[key: string]: SanctuarySession[]} = {};

    props.establishments.forEach((establishment) => {
        hourRange.forEach(hour => {
            const key = establishment.id + '_' + hour;

            detailledPlanning[key] = (props.planning[establishment.id] ?? []).filter(session => {
                return session.hour == hour;
            })
        })
    })

    return detailledPlanning;
});

function sessionTime(session: SanctuarySession) {

}

const mySessionsMap = computed<Record<string, SportSession>>(() => {
    const map: Record<string, SportSession> = {};
    if (props.mySessions) {
        for (const ms of props.mySessions) {
            if (ms.uid) {
                map[ms.uid] = ms;
            }
        }
    }
    return map;
});

const getSessionBookings = (sessionId: number) => {
    return mySessionsMap.value['sanctuary_' + sessionId] || null;
};
</script>

<template>
    <div :class="{'tab-pane fade': true, 'show active': props.active}" :id="props.tabId + '-tab-pane'" role="tabpanel" :aria-labelledby="props.tabId + '-tab'" tabindex="0">
        <table class="table table-bordered table-striped">
            <thead>
            <tr>
                <th style="width: 50px "></th>
                <th v-for="establishment in establishments" :key="establishment.id">{{ establishment.fullTitle }}</th>
            </tr>
            </thead>
            <tbody>
                <tr v-for="hour in hourRange">
                    <th>{{ hour }}</th>
                    <td v-for="establishment in establishments" :key="establishment.id + '_' + hour">

                        <template v-for="session in sessionsByEstablishmentAndTime[establishment.id + '_' + hour]" :key="session.id">
                            <a v-for="booking in [getSessionBookings(session.id)]"
                               :key="'booking-' + session.id"
                               :href="(booking && !booking.isShared && booking.cancelUrl) ? booking.cancelUrl : 'https://backoffice.bsport.io/booker-module-s/'+establishment.company.id+'/'+session.id+'?membership='+establishment.company.id" 
                               target="_blank" 
                               class="session btn btn-default" 
                               :class="{ 'booked': booking }"
                               :style="'height: ' + Math.floor(session.duration/60*100)+ '%; top: '+ session.minute/60*100 + '%'">
                                
                                <div class="session-content">
                                    {{ session.activity}}
                                    <i class="m-2">{{ session.coachName }}</i><br />

                                    {{session.hour }}H{{(session.minute + '').padStart(2, '0')}}
                                    - {{session.duration}}min
                                    - {{session.creditPrice}} credit -
                                    
                                    <span v-if="!session.full && !booking">Reserver</span>
                                    <span v-else-if="!session.waitingListFull && !booking">Liste d'attente</span>
                                    <span v-else-if="!booking">Complet</span>

                                    <ParticipantBadges 
                                        v-if="booking"
                                        :participants="booking.participants"
                                        :isShared="booking.isShared"
                                        size="sm"
                                        :showText="true"
                                    />
                                </div>
                            </a>
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped>
.hub-0 + .hub-1,
.hub-1 + .hub-2,
.hub-2 + .hub-3,
.hub-3 + .hub-4,
.hub-4 + .hub-5
{
    border-left: 2px solid rgb(150, 150, 150);
}

.session {
}

.session {
    display: block;
    border: 1px solid black;
    background-color: lightblue;
    text-align: center;
    position: absolute;
    left: 0;
    width: 100%;
    padding: 0;
    font-size: 0.9em;
    z-index: 10;
}

.session:hover {
    background-color: skyblue;
    z-index: 20;
}

.table {
    table-layout: fixed;
}

tbody td {
    height: 70px;
    position: relative;
    padding: 0;
}

.session-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
}
</style>
